import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Container, DataGrid, KeenIcon } from '@/components';
import {
  useGetNotificationModulesQuery,
  useGetNotificationRulesQuery,
  useUpdateNotificationRuleMutation,
} from '@/services/NotificationPreferencesService';
import { skipToken } from '@reduxjs/toolkit/query';
import { NotificationUserRoles } from '@/types/notification-preferences.types';
import { useNavigate } from 'react-router-dom';
import usePagination from '@/hooks/usePagination';
import type { ColumnDef } from '@tanstack/react-table';
import { usePermissions } from '@/hooks/usePermissions';

type ModuleItem = {
  id: string;
  name: string;
  icon: string;
};

type NotificationRecipient = {
  id: string;
  name: string;
  type: 'role' | 'user';
  isEnabled: boolean;
};

type NotificationPreferenceRow = {
  id: string | number;
  name: string;
  isEnabled: boolean;
  emailEnabled: boolean;
  isEmailConfigured: boolean;
  pushEnabled: boolean;
  isPushConfigured: boolean;
  recipients: NotificationRecipient[];
  emailTemplateId?: string;
  notificationTemplateId?: string;
  changedByUser?: {
    id: string;
    name: string;
  } | null;
};

const getModuleIcon = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower.includes('booking')) return 'calendar-add';
  if (lower.includes('payment')) return 'wallet';
  if (lower.includes('harbour')) return 'ship';
  if (lower.includes('club')) return 'bank';
  if (lower.includes('subscription')) return 'medal-star';
  if (lower.includes('post') || lower.includes('community')) return 'people';
  if (lower.includes('feed-back') || lower.includes('feedback'))
    return 'message-text-2';
  if (lower.includes('system')) return 'setting-2';
  if (lower.includes('kyc')) return 'shield-tick';
  return 'category';
};

const NotificationPreferences = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canReadRules = hasPermission('notification_rule_read');
  const canUpdateRules = hasPermission('notification_rule_update');
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [preferences, setPreferences] = useState<
    Record<string, NotificationPreferenceRow[]>
  >({});
  const [selectedNotification, setSelectedNotification] =
    useState<NotificationPreferenceRow | null>(null);
  const [moduleSearch, setModuleSearch] = useState('');

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    pagination,
    setPagination,
  } = usePagination();

  const { data: modulesResponse } = useGetNotificationModulesQuery({
    search: moduleSearch || undefined,
  });

  const modules = useMemo<ModuleItem[]>(() => {
    const apiModules = modulesResponse?.data;

    if (!Array.isArray(apiModules) || apiModules.length === 0) {
      return [];
    }

    return apiModules.map((m) => ({
      id: String(m.id),
      name: m.name ?? 'Unknown',
      icon: getModuleIcon(m.name ?? ''),
    }));
  }, [modulesResponse]);

  useEffect(() => {
    if (!modules.length) return;

    const exists = modules.some((m) => m.id === activeModule);
    if (!exists) {
      setActiveModule(modules[0].id);
    }
  }, [modules, activeModule]);

  const shouldFetchRules = !!activeModule && canReadRules;

  const {
    data: rulesResponse,
    isLoading: isRulesLoading,
    isFetching: isRulesFetching,
    refetch: refetchRules,
  } = useGetNotificationRulesQuery(
    shouldFetchRules
      ? {
          moduleId: activeModule || '',
          search: debouncedSearch.trim() || undefined,
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
        }
      : skipToken
  );

  const [updateNotificationRule] = useUpdateNotificationRuleMutation();

  const persistNotificationRule = async (row: NotificationPreferenceRow) => {
    if (!canUpdateRules) return;

    const rolePayload = row.recipients
      .filter((r) => r.type === 'role')
      .map((r) => {
        const match = /^role-(\d+)$/.exec(r.id);
        if (!match) return null;

        return {
          user_type: Number(match[1]),
          is_enabled: r.isEnabled,
        };
      })
      .filter(
        (value): value is { user_type: number; is_enabled: boolean } =>
          value !== null
      );

    try {
      await updateNotificationRule({
        id: String(row.id),
        body: {
          is_rule_enabled: row.isEnabled,
          is_email_notification_configured: row.isEmailConfigured,
          is_push_notification_configured: row.isPushConfigured,
          is_email_notification_enabled: row.emailEnabled,
          is_push_notification_enabled: row.pushEnabled,
          user_roles_involved: rolePayload,
        },
      }).unwrap();
      if (canReadRules && activeModule) {
        refetchRules();
      }
    } catch (error) {
      console.error('Failed to update notification rule', error);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, 500);

    return () => clearTimeout(t);
  }, [search, setPagination]);

  useEffect(() => {
    // When module changes, always go back to first page for rules
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [activeModule, setPagination]);

  useEffect(() => {
    if (!rulesResponse?.data || !activeModule || !canReadRules) return;

    handleTotalItems(rulesResponse.totalItems || 0);
    handleTotalPages(rulesResponse.totalPages || 1);

    const getRoleName = (role: number): string => {
      switch (role) {
        case NotificationUserRoles.ADMIN:
          return 'Admin';
        case NotificationUserRoles.COMMUNITY_MEMBERS:
          return 'Community Members';
        case NotificationUserRoles.MENTIONED_USERS:
          return 'Mentioned Users';
        case NotificationUserRoles.SUBSCRIBED_USER:
          return 'Subscribed User';
        case NotificationUserRoles.POI_REQUEST_USER:
          return 'POI Request User';
        case NotificationUserRoles.HARBOUR_REQUESTING_USER:
          return 'Harbour Requesting User';
        case NotificationUserRoles.CLUB_MEMBER:
          return 'Club Member';
        case NotificationUserRoles.BOATER:
          return 'Boater';
        case NotificationUserRoles.CLUB_MEMBER_REQUESTING_USER:
          return 'Club Member Requesting User';
        case NotificationUserRoles.CLUB_REQUESTER:
          return 'Club Requester';
        case NotificationUserRoles.HARBOUR_MANAGER:
          return 'Harbour Manager';
        case NotificationUserRoles.REGIONAL_OPERATOR:
          return 'Regional Operator';
        case NotificationUserRoles.YACHT_CLUB_ADMIN:
          return 'Yacht Club Admin';
        case NotificationUserRoles.MARINA_MANAGER:
          return 'Marina Manager';
        case NotificationUserRoles.SERVICE_PROVIDER:
          return 'Service Provider';
        case NotificationUserRoles.USER:
          return 'User';
        default:
          return `Role ${role}`;
      }
    };

    const transformed: NotificationPreferenceRow[] = rulesResponse.data.map(
      (rule): NotificationPreferenceRow => ({
        id: rule.id,
        name: rule.rule_name,
        isEnabled: rule.is_rule_enabled,
        emailEnabled: rule.is_email_notification_enabled,
        isEmailConfigured: rule.is_email_notification_configured,
        pushEnabled: rule.is_push_notification_enabled,
        isPushConfigured: rule.is_push_notification_configured,
        emailTemplateId: rule.email_template_id || undefined,
        notificationTemplateId: rule.notification_template_id || undefined,
        recipients: (rule.user_roles_involved || []).map<NotificationRecipient>(
          (role) => ({
            id: `role-${role.user_type}`,
            name: getRoleName(role.user_type),
            type: 'role',
            isEnabled: role.is_enabled,
          })
        ),
        changedByUser: rule.changed_by_user,
      })
    );

    setPreferences((prev) => ({
      ...prev,
      [activeModule]: transformed,
    }));
  }, [
    rulesResponse,
    activeModule,
    handleTotalItems,
    handleTotalPages,
    canReadRules,
  ]);

  const handleToggle = (
    id: string | number,
    field: 'isEnabled' | 'emailEnabled' | 'pushEnabled'
  ) => {
    if (!canUpdateRules) return;
    if (!activeModule) return;

    setPreferences((prev) => {
      const moduleKey = activeModule || '';
      const current = prev[moduleKey] || [];
      const updatedModuleData = current.map((item) => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: !item[field] };
          void persistNotificationRule(updatedItem);
          return updatedItem;
        }
        return item;
      });
      return { ...prev, [moduleKey]: updatedModuleData };
    });
  };

  const handleToggleRecipient = (
    notifId: string | number,
    recipientId: string
  ) => {
    if (!canUpdateRules) return;
    if (!activeModule) return;

    setPreferences((prev) => {
      const moduleKey = activeModule || '';
      const current = prev[moduleKey] || [];
      const updatedModuleData = current.map((item) => {
        if (item.id === notifId) {
          const updatedRecipients = item.recipients.map(
            (r: NotificationRecipient) =>
              r.id === recipientId ? { ...r, isEnabled: !r.isEnabled } : r
          );
          return { ...item, recipients: updatedRecipients };
        }
        return item;
      });
      return { ...prev, [moduleKey]: updatedModuleData };
    });
  };

  const handleSubmitRecipients = () => {
    if (!canUpdateRules) return;

    if (!activeModule) return;

    if (!selectedNotification) return;

    const updated = preferences[activeModule || '']?.find(
      (n) => n.id === selectedNotification.id
    );

    if (updated) {
      void persistNotificationRule(updated);
    }

    setSelectedNotification(null);
  };

  const columns = useMemo<ColumnDef<NotificationPreferenceRow>[]>(
    () => [
      {
        accessorFn: (row) => row.name,
        id: 'name',
        header: 'Notification Type',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">
              {row.original.name}
            </span>
          </div>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessorFn: (row) => row.isEnabled,
        id: 'roleEnabled',
        header: 'Role Enabled',
        cell: ({ row }) => (
          <div className="flex justify-center">
            <label className="switch switch-sm">
              <input
                type="checkbox"
                checked={row.original.isEnabled}
                className="cursor-pointer"
                disabled={!canUpdateRules}
                onChange={() => handleToggle(row.original.id, 'isEnabled')}
                aria-label={`Toggle role enabled for ${row.original.name}`}
              />
            </label>
          </div>
        ),
        meta: { className: 'w-[120px] text-center' },
      },
      {
        accessorFn: (row) => row.emailEnabled,
        id: 'emailEnabled',
        header: 'Email',
        cell: ({ row }) => (
          <div className="flex flex-col items-center gap-2">
            <label className="switch switch-sm">
              <input
                type="checkbox"
                checked={row.original.emailEnabled}
                className="cursor-pointer"
                disabled={!row.original.isEmailConfigured || !canUpdateRules}
                onChange={() => handleToggle(row.original.id, 'emailEnabled')}
                aria-label={`Toggle email notifications for ${row.original.name}`}
              />
            </label>
          </div>
        ),
        meta: { className: 'w-[100px] text-center' },
      },
      {
        accessorFn: (row) => row.pushEnabled,
        id: 'pushEnabled',
        header: 'Mobile Push',
        cell: ({ row }) => (
          <div className="flex flex-col items-center gap-2">
            <label className="switch switch-sm">
              <input
                type="checkbox"
                checked={row.original.pushEnabled}
                className="cursor-pointer"
                disabled={!row.original.isPushConfigured || !canUpdateRules}
                onChange={() => handleToggle(row.original.id, 'pushEnabled')}
                aria-label={`Toggle mobile push notifications for ${row.original.name}`}
              />
            </label>
          </div>
        ),
        meta: { className: 'w-[100px] text-center' },
      },
      {
        accessorFn: (row) => row.recipients,
        id: 'recipients',
        header: 'Recipients',
        cell: ({ row }) => {
          const visibleRecipients = row.original.recipients.slice(0, 3);
          const remainingCount = row.original.recipients.length - 3;

          return (
            <div className="flex flex-wrap items-center gap-1.5 py-1">
              {visibleRecipients.map((r: NotificationRecipient) => (
                <div
                  key={r.id}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border transition-all',
                    !r.isEnabled && 'opacity-40 grayscale',
                    r.type === 'role'
                      ? 'bg-blue-50 border-blue-100 text-blue-600 dark:bg-blue-500/10 dark:border-blue-500/20 contrast:bg-blue-500/10 contrast:border-blue-500/20 contrast:text-white'
                      : 'bg-purple-50 border-purple-100 text-purple-600 dark:bg-purple-500/10 dark:border-purple-500/20'
                  )}
                >
                  <KeenIcon
                    icon={r.type === 'role' ? 'profile-circle' : 'user'}
                    className="text-[10px]"
                  />
                  {r.name}
                </div>
              ))}
              {remainingCount > 0 && (
                <span className="text-[11px] font-bold text-gray-500 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full border border-gray-200 dark:border-gray-700">
                  +{remainingCount} more
                </span>
              )}
            </div>
          );
        },
        meta: { className: 'min-w-[300px]' },
      },
      {
        accessorFn: (row) => row.changedByUser,
        id: 'changed_by_user',
        header: 'Changed By User',
        cell: ({ row }) => (
          <div className="flex items-center justify-center text-sm text-gray-700 dark:text-gray-600">
            {row.original.changedByUser?.name || 'Rule not changed'}
          </div>
        ),
        meta: { className: 'w-[150px] text-center' },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex items-center gap-2 justify-center">
            <button
              type="button"
              onClick={() => setSelectedNotification(row.original)}
              className="btn btn-xs btn-light"
              title="Manage Recipients"
            >
              <KeenIcon icon="setting-4" className="text-xs" />
            </button>
            {row.original.emailTemplateId && (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/email-template-management/${row.original.emailTemplateId}`
                  )
                }
                className="btn btn-xs btn-light"
                title="View Email Template"
              >
                <KeenIcon icon="sms" className="text-xs" />
              </button>
            )}
            {row.original.notificationTemplateId && (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/notification-template-management/${row.original.notificationTemplateId}`
                  )
                }
                className="btn btn-xs btn-light"
                title="View Notification Template"
              >
                <KeenIcon icon="notification-on" className="text-xs" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[150px] text-center' },
      },
    ],
    [
      activeModule,
      canUpdateRules,
      handleToggle,
      handleToggleRecipient,
      navigate,
    ]
  );

  const hasActiveModule = modules.some((m) => m.id === activeModule);
  const currentData: NotificationPreferenceRow[] = hasActiveModule
    ? preferences[activeModule || ''] || []
    : [];
  const filteredData = currentData;

  return (
    <Container>
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-5">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-900">
              Notification Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-600 font-medium">
              Configure notification delivery and recipients per module.
            </p>
          </div>
        </div>

        {/* Module selector as top buttons */}
        <div className="card border-gray-100 dark:border-coal-700">
          <div className="card-body flex flex-col gap-3">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="flex items-center gap-2">
                <KeenIcon
                  icon="category"
                  className="text-gray-400 dark:text-gray-500"
                />
                <span className="text-sm font-semibold text-gray-800 dark:text-white">
                  Modules
                </span>
              </div>
              <div className="input input-sm h-9 flex items-center bg-gray-100 dark:bg-coal-700 border-gray-100 dark:border-coal-700 max-w-xs">
                <KeenIcon
                  icon="magnifier"
                  className="text-gray-400 dark:text-gray-500"
                />
                <input
                  type="text"
                  placeholder="Search module..."
                  className="w-full h-8 text-sm input border-0 scale-y-[0.9] rounded-none bg-transparent"
                  value={moduleSearch}
                  onChange={(e) => setModuleSearch(e.target.value)}
                  aria-label="Search modules"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 overflow-x-auto custom-scrollbar pt-1">
              {modules.length === 0 ? (
                <div className="px-2 py-1 text-sm text-gray-500 dark:text-gray-600">
                  No modules found
                </div>
              ) : (
                modules.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setActiveModule(m.id)}
                    className={clsx(
                      'inline-flex items-center gap-2 px-4 py-2 rounded-md border text-sm transition-colors',
                      activeModule === m.id
                        ? 'bg-primary text-white border-primary shadow-sm'
                        : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                    )}
                  >
                    <KeenIcon
                      icon={m.icon}
                      className={clsx(
                        'text-base',
                        activeModule === m.id
                          ? 'text-white'
                          : 'text-gray-400 group-hover:text-primary'
                      )}
                    />
                    <span className="font-semibold truncate max-w-[140px]">
                      {m.name}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Content Table - full width */}
        <div className="card card-grid border-0 shadow-sm overflow-hidden">
          <div className="card-header py-4 px-6 border-b border-gray-100 dark:border-coal-700 flex-wrap gap-4 items-center justify-between">
            {modules.length > 0 && (
              <h2 className="card-title text-base font-bold text-gray-900 dark:text-gray-500">
                {modules.find((m) => m.id === activeModule)?.name}
              </h2>
            )}
            <div className="flex items-center gap-4">
              <div className="input input-sm h-10 flex items-center bg-gray-100 dark:bg-coal-700 border-gray-100 dark:border-coal-700">
                <KeenIcon
                  icon="magnifier"
                  className="text-gray-400 dark:text-gray-500"
                />
                <input
                  type="text"
                  placeholder="Search notification..."
                  className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none bg-transparent"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search notifications"
                />
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <DataGrid<NotificationPreferenceRow>
              columns={columns}
              data={filteredData}
              loading={isRulesLoading || isRulesFetching}
              serverSide
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                totalItems: rulesResponse?.totalItems ?? totalItems,
                totalPages: rulesResponse?.totalPages ?? totalPages,
                setPagination,
                sizes: [5, 10, 25, 50],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
            />
          </div>
        </div>
      </div>

      {/* Recipient Management Modal (Static Mock) */}
      {selectedNotification && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="card w-full max-w-lg border-gray-100 dark:border-coal-700 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="card-header py-4 px-6 flex items-center justify-between border-b border-gray-100 dark:border-coal-600">
              <div className="flex flex-col">
                <h2 className="card-title text-base font-bold text-gray-900 dark:text-gray-500">
                  Recipient Settings
                </h2>
                <span className="text-xs text-gray-500 dark:text-gray-600 font-medium">
                  {selectedNotification.name}
                </span>
              </div>
              <button
                onClick={() => setSelectedNotification(null)}
                className="btn btn-icon btn-sm btn-light btn-clear hover:bg-gray-100 dark:hover:bg-coal-600 rounded-full"
                aria-label="Close recipient settings modal"
              >
                <KeenIcon icon="cross" />
              </button>
            </div>
            <div className="card-body p-0 max-h-[450px] overflow-y-auto custom-scrollbar">
              <div className="flex flex-col">
                {(preferences[activeModule || ''] || [])
                  .find((n) => n.id === selectedNotification.id)
                  ?.recipients.map((r: NotificationRecipient) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-coal-600 contrast:hover:bg-coal-600 transition-colors border-b border-gray-50 dark:border-coal-600 last:border-0 group"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={clsx(
                            'size-9 rounded-lg flex items-center justify-center text-xs font-bold transition-all',
                            r.type === 'role'
                              ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400'
                              : 'bg-purple-100 text-purple-600 dark:bg-purple-500/10 dark:text-purple-400'
                          )}
                        >
                          {r.name.charAt(0)}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-800 dark:text-gray-500 group-hover:text-primary dark:group-hover:text-blue-200 transition-colors contrast:group-hover:text-blue-200">
                            {r.name}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <KeenIcon
                              icon={
                                r.type === 'role' ? 'profile-circle' : 'user'
                              }
                              className="text-[10px] text-gray-400 dark:text-gray-500"
                            />
                            <span className="text-[10px] text-gray-500 dark:text-gray-600 capitalize font-medium">
                              {r.type}
                            </span>
                          </div>
                        </div>
                      </div>
                      <label className="switch switch-sm">
                        <input
                          type="checkbox"
                          checked={r.isEnabled}
                          disabled={!canUpdateRules}
                          onChange={() =>
                            handleToggleRecipient(selectedNotification.id, r.id)
                          }
                          aria-label={`Toggle recipient ${r.name}`}
                          className="cursor-pointer"
                        />
                      </label>
                    </div>
                  ))}
              </div>
            </div>
            <div className="card-footer py-4 px-6 flex justify-end gap-3 border-t border-gray-100 dark:border-coal-700">
              <button
                onClick={() => setSelectedNotification(null)}
                className="btn btn-sm btn-light"
              >
                Close
              </button>
              <button
                onClick={handleSubmitRecipients}
                className="btn btn-sm btn-primary"
                disabled={!canUpdateRules}
              >
                Submit Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default NotificationPreferences;
